import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { getErrorMessage } from '../lib/statusConfig';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  FileText,
  ArrowUpRight,
  Layers,
  Tag,
  RefreshCw,
  MessageSquare,
  AlignLeft,
  Cpu,
  Users,
  BadgeCheck,
  XCircle,
  Hourglass,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Avatar,
  Spinner,
} from '../components/ui';
import { blogsApi } from '../lib/api';
import { formatDateTime, estimateReadingTime } from '../lib/utils';
import type { JobStatus, JobBatchJobItem } from '../types';

// ─── Status Config ─────────────────────────────────────────────────────────────
const statusConfig: Record<
  JobStatus,
  { label: string; variant: 'warning' | 'info' | 'success' | 'danger'; icon: React.ReactNode; barColor: string }
> = {
  pending: {
    label: 'Pending',
    variant: 'warning',
    icon: <Clock className="h-3.5 w-3.5" />,
    barColor: 'bg-yellow-400',
  },
  processing: {
    label: 'Processing',
    variant: 'info',
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
    barColor: 'bg-blue-500',
  },
  completed: {
    label: 'Completed',
    variant: 'success',
    icon: <CheckCircle className="h-3.5 w-3.5" />,
    barColor: 'bg-green-500',
  },
  failed: {
    label: 'Failed',
    variant: 'danger',
    icon: <AlertCircle className="h-3.5 w-3.5" />,
    barColor: 'bg-red-500',
  },
  retrying: {
    label: 'Retrying',
    variant: 'warning',
    icon: <RefreshCw className="h-3.5 w-3.5 animate-spin" />,
    barColor: 'bg-orange-400',
  },
};

function getConfig(status: string) {
  if (status.startsWith('retrying')) return statusConfig['retrying'];
  return statusConfig[status as JobStatus] ?? statusConfig['pending'];
}

function isActiveStatus(status: string) {
  return status === 'pending' || status === 'processing' || status.startsWith('retrying');
}

// ─── Blog Card ─────────────────────────────────────────────────────────────────
const BlogCard: React.FC<{ job: JobBatchJobItem; index: number; createdBy: string }> = ({
  job,
  index,
  createdBy,
}) => {
  const cfg = getConfig(job.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
    >
      <Card hover padding="none" className="group overflow-hidden h-full flex flex-col">
        {/* Status bar */}
        <div className={`h-1 ${cfg.barColor}`} />

        <div className="p-5 flex flex-col flex-1">
          {/* Status + open arrow */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={cfg.variant} icon={cfg.icon}>{cfg.label}</Badge>
              {job.status === 'completed' && job.approval_status === 'approved' && (
                <Badge variant="success" icon={<BadgeCheck className="h-3.5 w-3.5" />} size="sm">Approved</Badge>
              )}
              {job.status === 'completed' && job.approval_status === 'rejected' && (
                <Badge variant="danger" icon={<XCircle className="h-3.5 w-3.5" />} size="sm">Rejected</Badge>
              )}
              {job.status === 'completed' && job.approval_status === 'pending_review' && (
                <Badge variant="warning" icon={<Hourglass className="h-3.5 w-3.5" />} size="sm">Pending Review</Badge>
              )}
            </div>
            {job.status === 'completed' && (
              <Link to={`/blogs/${job.job_id}`}>
                <ArrowUpRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            )}
          </div>

          {/* Title */}
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex-1">
            {job.selected_title}
          </h3>

          {/* Completed: word count + reading time */}
          {job.status === 'completed' && job.result && (
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <span>{job.result.word_count.toLocaleString()} words</span>
              <span>·</span>
              <span>{estimateReadingTime(job.result.word_count)}</span>
            </div>
          )}

          {/* Processing / Retrying: progress bar */}
          {(job.status === 'processing' || job.status === 'retrying') && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-2">
                <Spinner size="sm" />
                <span>{job.status === 'retrying' ? 'Retrying...' : 'Generating content...'}</span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: '60%' }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </div>
          )}

          {/* Pending */}
          {job.status === 'pending' && (
            <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 mb-4">
              <Clock className="h-4 w-4" />
              <span>Waiting in queue...</span>
            </div>
          )}

          {/* Failed: error message */}
          {job.status === 'failed' && job.error && (
            <div className="mb-4 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
                {getErrorMessage(job.error, job.error_code)}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto">
            <div className="flex items-center gap-2">
              <Avatar name={createdBy} size="sm" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{createdBy}</span>
            </div>
            {job.status === 'completed' && (
              <Link to={`/blogs/${job.job_id}`}>
                <Button variant="ghost" size="sm" rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}>
                  View
                </Button>
              </Link>
            )}
            {job.status === 'failed' && (
              <Link to="/generate">
                <Button variant="outline" size="sm">Retry</Button>
              </Link>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// ─── Skeleton Cards ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Card padding="none" className="overflow-hidden animate-pulse">
    <div className="h-1 bg-gray-200 dark:bg-gray-700" />
    <div className="p-5 space-y-3">
      <div className="h-5 w-20 skeleton rounded-full" />
      <div className="h-5 w-full skeleton rounded" />
      <div className="h-4 w-3/4 skeleton rounded" />
      <div className="h-4 w-1/2 skeleton rounded" />
    </div>
  </Card>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
export const JobSessionDetail: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => blogsApi.getBatchDetail(parseInt(batchId!)),
    // Keep polling as long as any job is still active
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasActive = data.jobs.some((j) => isActiveStatus(j.status));
      return hasActive ? 8000 : false;
    },
    enabled: !!batchId,
  });

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link to="/job-sessions">
          <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back to Job Sessions
          </Button>
        </Link>
        <div className="mt-4">
          <Card className="animate-pulse">
            <CardContent className="p-6 space-y-4">
              <div className="h-4 w-20 skeleton rounded" />
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-7 w-20 skeleton rounded-full" />)}
              </div>
              <div className="h-4 w-64 skeleton rounded" />
            </CardContent>
          </Card>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-5">
                <div className="h-8 w-12 skeleton rounded mb-1" />
                <div className="h-3 w-20 skeleton rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  // ── Error / Not Found ──
  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Session not found
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            This job session doesn't exist or you don't have access.
          </p>
          <Link to="/job-sessions">
            <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to Sessions
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const completedCount = session.jobs.filter((j) => j.status === 'completed').length;
  const inProgressCount = session.jobs.filter((j) => isActiveStatus(j.status)).length;
  const failedCount = session.jobs.filter((j) => j.status === 'failed').length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Back ── */}
      <Link to="/job-sessions" className="">
        <Button variant="ghost" leftIcon={<ArrowLeft className="h-4 w-4" />}>
          Back to Job Sessions
        </Button>
      </Link>

      {/* ── Session Hero ── */}
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
        <Card className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 border-primary-200 dark:border-primary-800/40">
          <CardContent className="p-6">

            {/* Keywords */}
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-4 w-4 text-primary-500" />
              <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
                Keywords
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {session.keywords.map((kw) => (
                <Badge key={kw} variant="primary" size="lg">{kw}</Badge>
              ))}
            </div>

            {/* Generation Settings Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-white/60 dark:bg-gray-800/40 rounded-xl border border-primary-100 dark:border-primary-800/30">
              <div className="flex items-start gap-2">
                <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/40 mt-0.5">
                  <MessageSquare className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Tone</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize mt-0.5">
                    {session.tone}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/40 mt-0.5">
                  <AlignLeft className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Word Count</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5">
                    {session.word_count.toLocaleString()} words
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/40 mt-0.5">
                  <Cpu className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">AI Provider</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize mt-0.5">
                    {session.llm_provider}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/40 mt-0.5">
                  <Users className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Audience</p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize mt-0.5 line-clamp-1">
                    {session.target_audience}
                  </p>
                </div>
              </div>
            </div>

            {/* Meta: date + created by */}
            <div className="flex flex-wrap items-center gap-5 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {formatDateTime(session.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Avatar name={session.created_by} size="sm" />
                Created by{' '}
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {session.created_by}
                </span>
              </span>
            </div>

          </CardContent>
        </Card>
      </motion.div>

      {/* ── Stats Strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Blogs', value: session.jobs.length, icon: <Layers className="h-5 w-5 text-primary-600" />, color: 'bg-primary-100 dark:bg-primary-900/30' },
          { label: 'Completed', value: completedCount, icon: <CheckCircle className="h-5 w-5 text-green-600" />, color: 'bg-green-100 dark:bg-green-900/30' },
          { label: 'In Progress', value: inProgressCount, icon: <Clock className="h-5 w-5 text-blue-600" />, color: 'bg-blue-100 dark:bg-blue-900/30' },
          { label: 'Failed', value: failedCount, icon: <AlertCircle className="h-5 w-5 text-red-600" />, color: 'bg-red-100 dark:bg-red-900/30' },
        ].map((stat) => (
          <Card key={stat.label} className="relative overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">{stat.value}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${stat.color}`}>{stat.icon}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Blog Cards ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-400" />
            Generated Blogs
          </h2>
          <div className="flex items-center gap-2">
            {inProgressCount > 0 && (
              <span className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Auto-updating...
              </span>
            )}
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {session.jobs.length} {session.jobs.length === 1 ? 'blog' : 'blogs'}
            </span>
          </div>
        </div>

        {session.jobs.length === 0 ? (
          <Card className="text-center py-12">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No blogs in this session.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {session.jobs.map((job, index) => (
              <BlogCard
                key={job.job_id}
                job={job}
                index={index}
                createdBy={session.created_by}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
